import { RefObject, useRef } from 'react';
import { EmailContentDefinition } from 'modules/graphql/types';
import { constructFieldErrors, getIsEditMode } from 'modules/templateEditor/common/utils';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { useTranslations } from 'modules/templateEditor/hooks/useTranslations';
import { useErrorValidationContext } from '../../context/ErrorValidationContext';
import { TextArea } from '.';
import { FieldNames, MediumDataKey } from '../../common/types';
import { ErrorInlineText } from '../../common/ErrorInlineText';

type TextAreaFieldAttributes = Pick<EmailContentDefinition, 'preheaderText' | 'subject'>;
type TextAreaFieldMediumDataKey = keyof TextAreaFieldAttributes;

type TextAreaFieldProps = {
  mediumRef: RefObject<HTMLTextAreaElement> | null;
  mediumDataKey: TextAreaFieldMediumDataKey;
  labelText: string;
  placeholder?: string;
  hasError: boolean;
  hasWarning: boolean;
  errorText?: string | JSX.Element;
  warningText?: string | JSX.Element;
};

export const EMAIL_SUBJECT_MEDIUM_DATA_KEY: MediumDataKey = 'subject';
export const EMAIL_PREHEADER_MEDIUM_DATA_KEY: MediumDataKey = 'preheaderText';

export const SubjectAndPreheaderSection = () => {
  const { t } = useTranslations();
  const { view } = useQueryParamContext();
  const { localeContentErrors, localeContentWarnings } = useErrorValidationContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const emailData = (liveTemplate?.mediumData || {}) as EmailContentDefinition;
  const subjectRef = useRef<HTMLTextAreaElement | null>(null);
  const preHeaderRef = useRef<HTMLTextAreaElement | null>(null);

  const { hasError: hasSubjectError, hasWarning: hasSubjectWarning } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.SUBJECT,
  });

  const { hasError: hasPreheaderError, hasWarning: hasPreheaderWarning } = constructFieldErrors({
    contentErrors: localeContentErrors,
    contentWarnings: localeContentWarnings,
    fieldName: FieldNames.PREHEADER_TEXT,
  });

  const subjectErrorText = isEditModeVisible ? (
    <ErrorInlineText hasError={hasSubjectError} hasWarning={hasSubjectWarning} />
  ) : undefined;

  const preheaderErrorText = isEditModeVisible ? (
    <ErrorInlineText hasError={hasPreheaderError} hasWarning={hasPreheaderWarning} />
  ) : undefined;

  const emailHeaderInformationFields: TextAreaFieldProps[] = [
    {
      mediumRef: subjectRef,
      mediumDataKey: EMAIL_SUBJECT_MEDIUM_DATA_KEY,
      labelText: 'label.subject',
      hasError: hasSubjectError,
      hasWarning: hasSubjectWarning,
      errorText: subjectErrorText,
    },
    {
      mediumRef: preHeaderRef,
      mediumDataKey: EMAIL_PREHEADER_MEDIUM_DATA_KEY,
      labelText: 'label.preheader',
      hasError: hasPreheaderError,
      hasWarning: hasPreheaderWarning,
      errorText: preheaderErrorText,
    },
  ];

  return (
    <>
      {emailHeaderInformationFields.map(
        ({ mediumRef, mediumDataKey, placeholder, labelText, hasError, hasWarning, errorText }) => (
          <TextArea
            key={mediumDataKey}
            mediumRef={mediumRef}
            mediumDataKey={mediumDataKey}
            mediumDataValue={emailData?.[mediumDataKey]}
            isEditModeVisible={isEditModeVisible}
            labelText={t(labelText)}
            placeholderText={t(placeholder || labelText)}
            hasError={hasError}
            hasWarning={hasWarning}
            errorText={errorText}
            warningText={errorText}
            onChangeHandler={(newVal) => setCurrentMediumField(mediumDataKey, newVal)}
          />
        ),
      )}
    </>
  );
};
